import { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Form, Header, Icon, Input, Label, Message, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import { useBooking } from "../../context/BookingContext";
import StatusBadge from "./StatusBadge";
import GuestHouseDropdown from "./GuestHouseDropdown";
import DataTable from "./DataTable";
import BookingFormDetailModal from "./BookingFormDetailModal";
import LastUpdatedBadge from "./LastUpdatedBadge";
import DepartureCell, { ArrivalCell } from "./DepartureCell";
import {
  allGuestNames,
  bookingArrival,
  bookingDeparture,
  bookingRowStyle,
  buildSearchHaystack,
  compareByArrival,
  compareByDeparture,
  fmtDateTime,
  matchesSearch,
  todayStr,
} from "./adminHelpers";

const STATUS_OPTIONS = [
  "Pending",
  "Approved",
  "Partially Confirmed",
  "Checked In",
  "Checked Out",
  "No Show",
  "Rejected",
  "Cancelled",
  "Transferred",
];

const STATUS_SELECT_OPTIONS = [
  { key: "All", value: "All", text: "All Statuses" },
  ...STATUS_OPTIONS.map((s) => ({ key: s, value: s, text: s })),
];

const columnHelper = createColumnHelper();

export default function BookingsTab({
  rooms,
  guestHouses,
  onOpenDetail,
  onOpenView,
  onCloseView,
  openBookingId = null,
  statusFilter = "All",
  ghFilter = "All",
  checkInFrom = "",
  checkInTo = "",
  searchQuery = "",
  onFilterChange,
  viewMode = false,
}) {
  const { bookings, apiError, apiLoading, lastSyncedAt, reloadFromApi } = useBooking();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reloadFromApi();
    } finally {
      setRefreshing(false);
    }
  };

  /* ── UI state ───────────────────────────────────────── */
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [filterOpen, setFilterOpen] = useState(false);
  /* Resolved from the already-loaded `bookings` array by the id the URL
     carries (`openBookingId`) — this widget is view-only, so unlike the
     editable detail there's nothing to fetch. */
  const viewBooking = openBookingId
    ? bookings.find((b) => String(b.id) === String(openBookingId)) || null
    : null;

  const resetFilters = () => {
    onFilterChange({ status: "All", from: "", to: "" });
  };

  const activeFilterCount = [
    statusFilter !== "All",
    checkInFrom !== "",
    checkInTo !== "",
  ].filter(Boolean).length;

  /* ── Client-side filtering ──────────────────────────── */
  const filtered = useMemo(() => {
    const q = searchQuery.trim();
    return bookings
      .filter((b) => {
        /* A booking's rooms can span more than one guest house, so search and
           the guest-house filter both match against every house holding a room
           (`stayGuestHouse*`, derived server-side) as well as the house the
           indent was filed with (`guestHouseName`/`guestHouseId`) — a GH admin
           looking for "their" bookings means either sense. */
        const stayGhIds = (b.stayGuestHouseIds || []).map(Number);
        const stayGhNames = b.stayGuestHouseNames || [];
        if (q && !matchesSearch(
          buildSearchHaystack([
            b.bookingCode,
            b.applicantName,
            ...allGuestNames(b),
            b.guestHouseName,
            ...stayGhNames,
            b.status,
          ]),
          q,
        )) return false;
        if (
          ghFilter !== "All" &&
          Number(b.guestHouseId) !== Number(ghFilter) &&
          !stayGhIds.includes(Number(ghFilter))
        ) return false;
        if (statusFilter !== "All" && b.status !== statusFilter) return false;
        const arrivalDate = bookingArrival(b).date;
        if (checkInFrom && arrivalDate < checkInFrom) return false;
        if (checkInTo   && arrivalDate > checkInTo)   return false;
        return true;
      })
      .sort(compareByArrival);
  }, [bookings, searchQuery, ghFilter, statusFilter, checkInFrom, checkInTo]);

  /* Commit to the URL a beat behind the keystroke, not per character — see
     the matching note above `filtered`. `searchQuery` (the committed value)
     is a prop now, so the sync back the other way (a browser Back/Forward
     landing on a different `q`) has to be explicit too. */
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchQuery) onFilterChange({ q: searchInput });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  /* Open the list at today rather than at the oldest booking on file; earlier
     arrivals load as the reader scrolls up into them. todayStr() is called per
     row rather than captured, so a dashboard left open past midnight
     re-anchors on the next reload. A booking with no arrival date reads as
     "past" here while compareByArrival sorts it last — harmless, since the
     search for the anchor stops far above those rows. */
  const firstCurrentArrival = useCallback((b) => (bookingArrival(b).date || "") >= todayStr(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("bookingCode", {
        header: "Booking ID",
      }),
      columnHelper.accessor("applicantName", {
        header: "Applicant Name",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("guestName", {
        header: "Guest Name",
        cell: (info) => info.getValue() || "—",
      }),
      /* Where the guest is actually staying — every house holding a room, not
         the house the indent happened to be filed with. Accessor (not just a
         cell renderer) so sorting and the global search key off the same text
         the column shows. The server already falls this back to the filed
         house while no room is allotted. */
      columnHelper.accessor((b) => (b.stayGuestHouseNames || []).join(", "), {
        id: "guestHouseName",
        header: "Guest House",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("checkIn", {
        header: "Arrival",
        /* The room's own start once it was moved (see bookingArrival). */
        cell: (info) => <ArrivalCell booking={info.row.original} />,
        /* The accessor stays the bare booked date for the column id, but
           that makes react-table's own
           comparison blind to the time and shuffles a single day's arrivals
           arbitrarily. Sorting on the same key the default order uses means
           clicking "Arrival" ascending reproduces the opening list exactly. */
        sortingFn: (rowA, rowB) => compareByArrival(rowA.original, rowB.original),
      }),
      columnHelper.accessor((b) => bookingDeparture(b).key, {
        id: "checkOut",
        header: "Departure",
        cell: (info) => <DepartureCell booking={info.row.original} />,
        sortingFn: (rowA, rowB) => compareByDeparture(rowA.original, rowB.original),
      }),
      columnHelper.accessor("numberOfDays", {
        header: "Days",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor((b) => b.guests || b.roomCount || "—", {
        id: "rooms",
        header: "Rooms",
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (info) => fmtDateTime(info.getValue()),
      }),
      columnHelper.display({
        id: "actions",
        header: "Action",
        enableSorting: false,
        cell: (info) => {
          const b = info.row.original;
          return viewMode ? (
            <Button size="small" basic onClick={(e) => { e.stopPropagation(); onOpenView(b); }}>
              <Icon name="eye" /> View
            </Button>
          ) : (
            <Button size="small" basic onClick={(e) => { e.stopPropagation(); onOpenDetail(b); }}>
              <Icon name="pencil alternate" /> Edit
            </Button>
          );
        },
      }),
    ],
    [viewMode, onOpenDetail, onOpenView],
  );

  const rowStyleFor = (booking) => bookingRowStyle(booking);

  return (
    <div>
      {/* ── Top bar ─────────────────────────────────────── */}
      <Segment basic vertical>
        <Header as="h3" content="Booking Requests" />
        <Form>
          <Form.Group>
            <Form.Field>
              <GuestHouseDropdown
                value={ghFilter === "All" ? "All" : Number(ghFilter)}
                options={[
                  { value: "All", label: "All Guest Houses" },
                  ...guestHouses.map((g) => ({ value: g.id, label: g.name })),
                ]}
                onChange={(v) => onFilterChange({ gh: v === "All" ? "All" : Number(v) })}
              />
            </Form.Field>
            <Form.Field width={6}>
              <Input
                icon="search"
                iconPosition="left"
                placeholder="Search by ID, name, guest house, status…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                action={
                  searchInput
                    ? { icon: "times circle", basic: true, onClick: () => setSearchInput("") }
                    : undefined
                }
              />
            </Form.Field>
            <Form.Field>
              <Button type="button" toggle active={filterOpen} onClick={() => setFilterOpen((o) => !o)} title="Filters">
                <Icon name="filter" />
                Filters
                {activeFilterCount > 0 && (
                  <Label circular color="blue" size="mini" style={{ marginLeft: 6 }}>
                    {activeFilterCount}
                  </Label>
                )}
              </Button>
            </Form.Field>
            <Form.Field>
              <Button type="button" icon onClick={handleRefresh} loading={refreshing} disabled={refreshing} title="Refresh">
                <Icon name="refresh" />
              </Button>
            </Form.Field>
            <Form.Field>
              <LastUpdatedBadge lastSyncedAt={lastSyncedAt} syncing={apiLoading} />
            </Form.Field>
          </Form.Group>

          {/* ── Filter panel ──────────────────────────────── */}
          {filterOpen && (
            <Segment secondary>
              <Form.Group widths="equal">
                <Form.Select
                  label="Status"
                  value={statusFilter}
                  options={STATUS_SELECT_OPTIONS}
                  onChange={(_, data) => onFilterChange({ status: data.value })}
                />
                <Form.Input
                  label="Arrival From"
                  type="date"
                  value={checkInFrom}
                  onChange={(e) => onFilterChange({ from: e.target.value })}
                />
                <Form.Input
                  label="Arrival To"
                  type="date"
                  value={checkInTo}
                  onChange={(e) => onFilterChange({ to: e.target.value })}
                />
              </Form.Group>
              {activeFilterCount > 0 && (
                <Button type="button" basic size="small" onClick={resetFilters}>
                  <Icon name="times" /> Clear Filters
                </Button>
              )}
            </Segment>
          )}
        </Form>
      </Segment>

      {/* ── Body ────────────────────────────────────────── */}
      {apiError && (
        <Message negative>
          <Icon name="exclamation triangle" />
          {apiError}
          <div style={{ marginTop: 12 }}>
            <Button type="button" basic onClick={handleRefresh}>
              <Icon name="refresh" /> Retry
            </Button>
          </div>
        </Message>
      )}
      <DataTable
        data={filtered}
        columns={columns}
        batchSize={25}
        anchorRow={firstCurrentArrival}
        getRowStyle={rowStyleFor}
        minWidth={1200}
        onRowClick={viewMode ? onOpenView : onOpenDetail}
        emptyMessage={
          bookings.length === 0
            ? "No bookings found."
            : "No bookings match your filters."
        }
      />

      {viewBooking && (
        <BookingFormDetailModal
          booking={viewBooking}
          rooms={rooms}
          guestHouses={guestHouses}
          onClose={onCloseView}
        />
      )}
    </div>
  );
}
